import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface FrankDebugInfo {
  memberId?: string;
  organizationId?: string;
  associationId?: number;
  apiUrl?: string;
  isEnabled?: boolean;
  memberFound: boolean;
  organizationFound: boolean;
  configFound: boolean;
  associationIdValid: boolean;
}

/**
 * Comprehensive debug function for Frank AI issues
 */
export const debugFrankSetup = async (): Promise<FrankDebugInfo> => {
  console.log("🔍 Starting Frank debug analysis...");
  
  const debugInfo: FrankDebugInfo = {
    memberFound: false,
    organizationFound: false,
    configFound: false,
    associationIdValid: false,
  };

  try {
    // Step 1: Check member context
    let memberId = sessionStorage.getItem('organization_member_id');
    if (!memberId) {
      memberId = localStorage.getItem('manager_member_id');
      if (memberId) {
        console.log("🔧 Found member ID in localStorage backup:", memberId);
        sessionStorage.setItem('organization_member_id', memberId);
      }
    }

    if (!memberId) {
      console.error("❌ No member ID found in session or local storage");
      toast.error("Debug: No member context found");
      return debugInfo;
    }

    debugInfo.memberId = memberId;
    debugInfo.memberFound = true;
    console.log("✅ Member ID found:", memberId);

    // Step 2: Check organization membership
    const { data: memberData, error: memberError } = await supabase
      .from('organization_members')
      .select('id, organization_id, full_name, email, role')
      .eq('id', memberId)
      .single();

    if (memberError || !memberData) {
      console.error("❌ Error fetching member data:", memberError);
      toast.error("Debug: Member not found in database");
      return debugInfo;
    }

    debugInfo.organizationId = memberData.organization_id;
    debugInfo.organizationFound = true;
    console.log("✅ Member data found:", {
      name: memberData.full_name,
      email: memberData.email,
      role: memberData.role,
      organizationId: memberData.organization_id
    });

    // Step 3: Check organization and association ID
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('id, name, association_id')
      .eq('id', memberData.organization_id)
      .single();

    if (orgError || !orgData) {
      console.error("❌ Error fetching organization data:", orgError);
      toast.error("Debug: Organization not found");
      return debugInfo;
    }

    debugInfo.associationId = orgData.association_id;
    debugInfo.associationIdValid = typeof orgData.association_id === 'number' && orgData.association_id > 0;
    
    console.log("✅ Organization data found:", {
      name: orgData.name,
      associationId: orgData.association_id,
      valid: debugInfo.associationIdValid
    });

    if (!debugInfo.associationIdValid) {
      console.error("❌ Invalid association ID:", orgData.association_id);
      toast.error(`Debug: Invalid association ID (${orgData.association_id})`);
      return debugInfo;
    }

    // Step 4: Check Frank API configuration
    const { data: configData, error: configError } = await supabase
      .from('frank_api_config')
      .select('api_url, is_enabled')
      .eq('organization_id', memberData.organization_id)
      .single();

    if (configError) {
      console.warn("⚠️ No Frank API config found for organization:", configError);
      toast.warning("Debug: No Frank API configuration found");
      return debugInfo;
    }

    debugInfo.apiUrl = configData.api_url;
    debugInfo.isEnabled = configData.is_enabled;
    debugInfo.configFound = true;
    
    console.log("✅ Frank API config found:", {
      apiUrl: configData.api_url,
      isEnabled: configData.is_enabled
    });

    if (!configData.is_enabled) {
      console.warn("⚠️ Frank API is disabled for this organization");
      toast.warning("Debug: Frank API is disabled");
      return debugInfo;
    }

    if (!configData.api_url) {
      console.error("❌ No API URL configured");
      toast.error("Debug: No API URL configured");
      return debugInfo;
    }

    console.log("🎉 Frank debug analysis completed successfully!");
    toast.success("Debug: Frank setup appears to be correctly configured");
    
    return debugInfo;

  } catch (error) {
    console.error("💥 Error during Frank debug analysis:", error);
    toast.error(`Debug error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return debugInfo;
  }
};

/**
 * Test Frank API connectivity
 */
export const testFrankConnectivity = async (apiUrl: string): Promise<{
  success: boolean;
  statusCode?: number;
  statusText?: string;
  responseTime?: number;
  error?: string;
}> => {
  console.log("🔌 Testing Frank API connectivity...");
  const startTime = Date.now();
  
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000); // 10s timeout
    
    const response = await fetch(apiUrl.replace('/ask', '/health') || `${apiUrl}/health`, {
      method: 'GET',
      signal: controller.signal,
    });
    
    clearTimeout(timeout);
    const responseTime = Date.now() - startTime;
    
    console.log(`✅ Connectivity test completed in ${responseTime}ms:`, {
      status: response.status,
      statusText: response.statusText
    });
    
    return {
      success: response.ok,
      statusCode: response.status,
      statusText: response.statusText,
      responseTime
    };
  } catch (error) {
    const responseTime = Date.now() - startTime;
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    
    console.error(`❌ Connectivity test failed after ${responseTime}ms:`, errorMessage);
    
    return {
      success: false,
      responseTime,
      error: errorMessage
    };
  }
};

/**
 * Enhanced association ID fetching with debug logging
 */
export const debugFetchAssociationId = async (): Promise<{
  success: boolean;
  associationId?: string;
  debug: FrankDebugInfo;
}> => {
  const debug = await debugFrankSetup();
  
  if (debug.associationIdValid && debug.associationId) {
    return {
      success: true,
      associationId: debug.associationId.toString(),
      debug
    };
  }
  
  return {
    success: false,
    debug
  };
};